import { HttpClient, HttpParams } from "@angular/common/http";
import { computed, inject, Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { AppConfigService } from "@api/services/app-config.service";

@Injectable({
    providedIn: "root",
})
export class ApiService {
    readonly baseUrl = computed(() => this.appConfig().apiHost);

    private readonly http = inject(HttpClient);
    private readonly configSvc = inject(AppConfigService);
    private readonly appConfig = this.configSvc.appConfig;

    /* eslint-disable @typescript-eslint/no-explicit-any*/
    get<Response>(endpoint: string, options?: { params?: HttpParams }): Observable<Response> {
        if (options?.params) {
            return this.http
                .get<Response>(`${this.baseUrl()}/${endpoint}`, { params: options.params })
                .pipe(map((response: any) => response.data));
        } else {
            return this.http.get<Response>(`${this.baseUrl()}/${endpoint}`).pipe(map((response: any) => response.data));
        }
    }

    post<Request, Response>(
        endpoint: string,
        data: Request | null,
        options?: { params?: HttpParams },
    ): Observable<Response>;
    post(endpoint: string): Observable<any>;
    post<Request, Response>(
        endpoint: string,
        data?: Request | null,
        options?: { params?: HttpParams },
    ): Observable<Response | any> {
        if (data === undefined) {
            return this.http.post(`${this.baseUrl()}/${endpoint}`, null);
        } else if (options?.params) {
            return this.http
                .post<Response>(`${this.baseUrl()}/${endpoint}`, data, { params: options.params })
                .pipe(map((response: any) => response?.data));
        } else {
            return this.http
                .post<Response>(`${this.baseUrl()}/${endpoint}`, data)
                .pipe(map((response: any) => response?.data));
        }
    }

    exportFile<Request>(endpoint: string, data: Request | null, options?: { params?: HttpParams }): Observable<Blob> {
        const url = `${this.baseUrl()}/${endpoint}`;

        const httpOptions: any = {
            params: options?.params,
            responseType: "blob" as const, // required for blob response
        };

        return this.http.post(url, data, httpOptions).pipe(map((response: any) => response as Blob));
    }

    put<Request, Response>(endpoint: string, data: Request | null): Observable<Response>;
    put(endpoint: string): Observable<any>;
    put<Request, Response>(endpoint: string, data?: Request | null): Observable<Response | any> {
        if (data === undefined) {
            return this.http.put(`${this.baseUrl()}/${endpoint}`, null);
        } else {
            return this.http
                .put<Response>(`${this.baseUrl()}/${endpoint}`, data)
                .pipe(map((response: any) => response?.data));
        }
    }

    delete<Response>(endpoint: string): Observable<Response>;
    delete(endpoint: string): Observable<any>;
    delete<Response>(endpoint: string): Observable<Response | any> {
        return this.http.delete<Response>(`${this.baseUrl()}/${endpoint}`).pipe(map((response: any) => response?.data));
    }
}
